import FormattedMessage from 'I18n/components/FormattedMessage';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { List } from 'foundations-components/transitional/components/List';
import { Fragment, useMemo } from 'react';
import ViewTypes from 'ui-addon-calendars/constants/CalendarViewTypes';
import { filterEventsByWeekday, getBoundsFromCalendarProps, getEventsGroupedByDay } from 'ui-addon-calendars/lib/eventList';
// @ts-expect-error eventParse has no typings yet
import { getSortedChildren } from 'ui-addon-calendars/lib/eventParse';
import { momentWithTimezone } from '../../lib/time';
import DayGroup from './DayGroup';
import { getEventsToDisplay } from '../../lib/eventDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ListCalendar({
  children,
  currentTime,
  displayDay,
  displayYear,
  hideWeekends,
  listViewMaxEventsPerDay,
  timezone,
  use,
  useExactEventTimes,
  eventDisplay
}) {
  const unitOfTime = useMemo(() => {
    switch (use) {
      case ViewTypes.DAY:
        return 'day';
      case ViewTypes.WEEK:
        return 'week';
      case ViewTypes.MONTH:
      default:
        return 'month';
    }
  }, [use]);
  const {
    start: startOfPeriod,
    end: endOfPeriod
  } = getBoundsFromCalendarProps({
    displayDay,
    displayYear,
    timezone,
    unitOfTime
  });
  const sortedChildren = useMemo(() => getSortedChildren(children, useExactEventTimes, timezone).toArray(), [children, useExactEventTimes, timezone]);
  const eventsToDisplay = useMemo(() => {
    return getEventsToDisplay({
      sortedChildren,
      eventDisplay,
      eventRange: {
        start: startOfPeriod.valueOf(),
        end: endOfPeriod.valueOf()
      }
    });
  }, [endOfPeriod, eventDisplay, sortedChildren, startOfPeriod]);
  const eventsGroupedByDays = useMemo(() => {
    return getEventsGroupedByDay({
      sortedChildren: eventsToDisplay.allEvents.toJS(),
      startOfPeriod,
      endOfPeriod
    });
  }, [eventsToDisplay.allEvents, startOfPeriod, endOfPeriod]);
  const filteredEventsGroupedByDays = useMemo(() => filterEventsByWeekday({
    eventsGroupedByDays,
    hideWeekends
  }), [eventsGroupedByDays, hideWeekends]);
  const currentDayIndex = useMemo(() => {
    const isInRange = startOfPeriod.isSameOrBefore(currentTime, 'day') && endOfPeriod.isSameOrAfter(currentTime, 'day');
    const currentTimestamp = Number(currentTime);
    if (!isInRange || !currentTimestamp) {
      return null;
    }
    const currentTimeMoment = momentWithTimezone(timezone, currentTimestamp);
    const index = filteredEventsGroupedByDays.findIndex(({
      day
    }) => day.isSameOrAfter(currentTimeMoment, 'day'));

    // We have already determined the current day is in range.
    // a found index of -1 means the current day is after the last daygroup
    return index === -1 ? filteredEventsGroupedByDays.length : index;
  }, [currentTime, startOfPeriod, endOfPeriod, filteredEventsGroupedByDays, timezone]);
  if (filteredEventsGroupedByDays.length === 0) {
    return /*#__PURE__*/_jsx(EmptyState, {
      "data-test-id": "list-view-empty-state",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "calendar.noEvents.title"
      }),
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "calendar.noEvents.subtitle"
      }),
      secondaryContent: /*#__PURE__*/_jsx(Illustration, {
        name: "empty-state-charts"
      })
    });
  }
  const dayAnchor = /*#__PURE__*/_jsx("div", {
    id: "calendar-list-view-today-anchor"
  });
  return /*#__PURE__*/_jsxs(List, {
    "data-test-id": `list-view--${use}`,
    wrapChildren: true,
    children: [filteredEventsGroupedByDays.map((dayGroup, index) => /*#__PURE__*/_jsxs(Fragment, {
      children: [currentDayIndex === index && dayAnchor, /*#__PURE__*/_jsx(DayGroup, {
        dayGroup: dayGroup,
        listViewMaxEventsPerDay: listViewMaxEventsPerDay
      })]
    }, dayGroup.day.toString())), currentDayIndex === filteredEventsGroupedByDays.length && dayAnchor]
  });
}